// src/pages/driverReports/statementComponents/CarSummary.js
import React, { useState } from "react";
import { exportAllSheetsToExcel } from "../carSheets/exportUtils";
export default function CarSummary({ month, sheets, drivers, clients }) {
  const [exporting, setExporting] = useState(false);
  const driversMap = React.useMemo(() => {
  const map = {};
  (drivers || []).forEach((d) => {
    map[d.id] = d;
  });
  return map;
}, [drivers]);
  // Driver lookup by NAME (income rows only have driver name)
  const driversByName = React.useMemo(() => {
    const map = {};
    (drivers || []).forEach((d) => {
      if (!d.fullName) return;
      const key = d.fullName.trim().toUpperCase();
      map[key] = d;
    });
    return map;
  }, [drivers]);

  // Manual trip clients (from Firestore `clients` collection)
const manualClientsSet = React.useMemo(() => {
  const set = new Set();

  (clients || []).forEach((c) => {
  if (!c.id) return;
  set.add(c.id.trim().toUpperCase());
});

  return set;
}, [clients]);


  const handleExportAll = async () => {
    try {
      setExporting(true);
      await exportAllSheetsToExcel(sheets, month);
    } catch (error) {
      console.error('Export all error:', error);
      alert(`Export failed: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };
  if (!sheets || sheets.length === 0) {
    return <div style={{ padding: 20 }}>No data available for summary.</div>;
  }

  const one = sheets[0];
  const monthLabel = one.monthLabel || month.toUpperCase();

  const num = (v) => (isNaN(Number(v)) ? 0 : Number(v));

  /* --------------------------------------------------------
     WEEK KEY NORMALIZATION (MAIN FIX FOR ALIGNMENT)
  -------------------------------------------------------- */
  function normalizeWeekKeys(row) {
    return {
      week1: num(row.week1 ?? row.W1 ?? row.w1 ?? row.week01 ?? 0),
      week2: num(row.week2 ?? row.W2 ?? row.w2 ?? row.week02 ?? 0),
      week3: num(row.week3 ?? row.W3 ?? row.w3 ?? row.week03 ?? 0),
      week4: num(row.week4 ?? row.W4 ?? row.w4 ?? row.week04 ?? 0),
      week5: num(row.week5 ?? row.W5 ?? row.w5 ?? row.week05 ?? 0),
      total: num(row.total ?? 0),
    };
  }

  /* --------------------------------------------------------
     SHORT COMPANY NAMES
  -------------------------------------------------------- */
  const SHORT_COMFORT = "Comfort Line";
  const SHORT_LOLIA = "Lolia";

  function shortCompanyName(full = "") {
    const v = (full || "").toLowerCase();
    if (v.includes("lolia")) return SHORT_LOLIA;
    if (v.includes("comfort")) return SHORT_COMFORT;
    return full || "";
  }

   /* ============================================================
   1️⃣ EXPENSES (FIXED) — CORRECTED
   ------------------------------------------------------------
   - Collect ALL expense labels from ALL sheets
   - Ignore summary rows completely
============================================================ */

// STEP 1: Collect ALL unique expense labels from ALL sheets
const allExpenseLabels = new Set();

sheets.forEach((sheet) => {
  (sheet.expensesRows || []).forEach((row) => {
    if (row.label && !row.isSummary) {
      allExpenseLabels.add(row.label.trim());
    }
  });
});

// STEP 2: Create array of all expense labels
const expenseTemplate = Array.from(allExpenseLabels);

// STEP 3: Calculate totals for each expense label
const expenses = expenseTemplate.map((label) => {
  let total = 0;

  sheets.forEach((sh) => {
    const row = (sh.expensesRows || []).find(
      (r) => r.label && r.label.trim() === label && !r.isSummary
    );
    if (row) total += num(row.total || 0);
  });

  return { 
    label: label, 
    total: total 
  };
});

 /* ============================================================
   2️⃣ BANK INCOME — FINAL ORDER:
   RIDE-HAILING → MANUAL CLIENTS (A–Z) → POS → ADJUSTMENTS
============================================================ */

const RIDE_HAILING = [
  "UBER",
  "CAREEM",
  "BOLT",
  "YANGO",
  "ZED",
];

const POS_PLATFORM = "POS";

const ADJUSTMENT_ROWS = [
  "OTHER ADJUSTMENT",
  "PAYMENT RECEIVED",
  "CASH RECEIVED / PAID TO DRIVER", // ⭐ ADD THIS LINE
];

// 1️⃣ Collect ALL income rows
const rawIncomeRows = sheets.flatMap((sh) => sh.incomeRows || []);

// 2️⃣ Ensure valid company name
const allIncomeRows = rawIncomeRows.filter((r) => {
  const c = (r.company || "").toUpperCase();
  return c !== "";
});

// 3️⃣ Extract unique company names
const uniqueCompanies = Array.from(
  new Set(allIncomeRows.map((r) => r.company.trim()))
);

// 4️⃣ Separate into groups
const rideHailingCompanies = RIDE_HAILING.filter((p) =>
  uniqueCompanies.includes(p)
);

// Manual clients = everything except ride-hailing, POS, adjustments
const manualClients = uniqueCompanies
  .filter(
    (c) =>
      !RIDE_HAILING.includes(c) &&
      c !== POS_PLATFORM &&
      !ADJUSTMENT_ROWS.includes(c)
  )
  .sort(); // alphabetical

const hasPOS = uniqueCompanies.includes(POS_PLATFORM) ? [POS_PLATFORM] : [];

const adjustmentCompanies = ADJUSTMENT_ROWS.filter((a) =>
  uniqueCompanies.includes(a)
);

// ⭐ FINAL ORDER ⭐
const orderedCompanies = [
  ...rideHailingCompanies,  // 1. UBER → ZED
  ...manualClients,         // 2. ALL CLIENTS A→Z
  ...hasPOS,                // 3. POS
  ...adjustmentCompanies,   // 4. Adjustments
];

// 5️⃣ Build output rows
const income = orderedCompanies.map((company) => {
  let w1 = 0,
    w2 = 0,
    w3 = 0,
    w4 = 0,
    w5 = 0,
    total = 0;

  allIncomeRows
    .filter((r) => r.company === company)
    .forEach((orig) => {
      const row = normalizeWeekKeys(orig);
      w1 += row.week1;
      w2 += row.week2;
      w3 += row.week3;
      w4 += row.week4;
      w5 += row.week5;
      total += row.total;
    });

  return {
    company,
    week1: w1,
    week2: w2,
    week3: w3,
    week4: w4,
    week5: w5,
    total,
  };
});


  /* ============================================================
     3️⃣ CASH TRIPS
  ============================================================ */

  const CASH_ORDER = [
    "UBER CASH",
    "CAREEM CASH",
    "YANGO CASH",
    "BOLT CASH",
    "ZED CASH",
    "OTHER CASH",
  ];

  const allCashRows = sheets.flatMap((sh) =>
    (sh.cashRows || []).filter((r) => {
      const c = (r.company || "").toUpperCase();
      if (!c || c === "") return false;
      if (c.includes("POS")) return false;
      return CASH_ORDER.includes(c);
    })
  );

  const uniqueCashCompanies = Array.from(
    new Set(allCashRows.map((r) => r.company))
  );

  const finalCashCompanies = CASH_ORDER.filter((p) =>
    uniqueCashCompanies.includes(p)
  );

  const cash = finalCashCompanies.map((company) => {
    let w1 = 0,
      w2 = 0,
      w3 = 0,
      w4 = 0,
      w5 = 0,
      total = 0;

    allCashRows
      .filter((r) => r.company === company)
      .forEach((orig) => {
        const row = normalizeWeekKeys(orig);

        w1 += row.week1;
        w2 += row.week2;
        w3 += row.week3;
        w4 += row.week4;
        w5 += row.week5;
        total += row.total;
      });

    return {
      company,
      week1: w1,
      week2: w2,
      week3: w3,
      week4: w4,
      week5: w5,
      total,
    };
  });

  /* ============================================================
   4️⃣ INTER-COMPANY SETTLEMENT
   - BANK settlement:
       • Manual trips + YANGO → Comfort Line
       • UBER / CAREEM / BOLT / ZED → Driver company
       • POS → Driver company (POS owner)
   - CASH settlement:
       • Cash always with driver (driverCompany → carCompany)
============================================================ */

const DRIVER_BANK_PLATFORMS = new Set([
  "UBER",
  "CAREEM",
  "BOLT",
  "ZED",
]);

// key: fromShort|toShort|platform → amount
const bankSettlementMap = {};
const cashSettlementMap = {};

sheets.forEach((sh) => {
  const carShort = shortCompanyName(sh.company || "");

  
  /* ----------------------------
     BANK SETTLEMENT (incomeRows)
  ----------------------------- */
  (sh.incomeRows || [])
  .filter((r) => r.rowType !== "summary")

  .forEach((orig) => {
    console.log("🧪 INCOME ROW DEBUG", {
  company: orig.company,
  driverId: orig.driverId,
  driver: orig.driver,
  driverUID: orig.driverUID,
  keys: Object.keys(orig),
});

    const platform = (orig.company || "").trim().toUpperCase();

    // Manual trip detection (based on Firestore clients)
// Normalize manual client key to match income rows & Firestore IDs
const manualClientKey = (orig.company || "")
  .trim()
  .toUpperCase()
  .replace(/\s+/g, "_");

const isManualTrip = manualClientsSet.has(manualClientKey);



    // ✅ CRITICAL: row → sheet fallback
    const driverNameKey = (orig.driver || "").trim().toUpperCase();

const driverDoc =
  driversMap[orig.driverId] ||
  driversByName[driverNameKey];


const driverCompanyName =
  driverDoc?.company || sh.company || "";

const driverShort = shortCompanyName(driverCompanyName);


    const weeks = normalizeWeekKeys(orig);
const total =
  weeks.total ||
  weeks.week1 +
    weeks.week2 +
    weeks.week3 +
    weeks.week4 +
    weeks.week5;

if (!total) return;

let bankShort = null;

// --------------------------------------------------
// Manual trips (Firestore clients) → Comfort
// --------------------------------------------------
if (isManualTrip) {
  bankShort = SHORT_COMFORT;

// --------------------------------------------------
// Yango → Comfort
// --------------------------------------------------
} else if (platform === "YANGO") {
  bankShort = SHORT_COMFORT;

// --------------------------------------------------
// POS → driver company (we’ll refine later if needed)
// --------------------------------------------------
} else if (platform.startsWith("POS")) {
  bankShort = driverShort;

// --------------------------------------------------
// Uber / Bolt / Careem / Zed → driver company
// --------------------------------------------------
} else if (DRIVER_BANK_PLATFORMS.has(platform)) {
  bankShort = driverShort;

// --------------------------------------------------
// Ignore everything else
// --------------------------------------------------
} else {
  return;
}

if (!bankShort || !carShort) return;
if (bankShort === carShort) return;

const key = `${bankShort}|${carShort}|${platform}`;
bankSettlementMap[key] = (bankSettlementMap[key] || 0) + total;

  });


  /* ----------------------------
     CASH SETTLEMENT (cashRows)
  ----------------------------- */
  (sh.cashRows || [])
  .filter((r) => r.rowType !== "summary")

  .forEach((orig) => {
    const platform = (orig.company || "").trim().toUpperCase();

    const driverNameKey = (orig.driver || "").trim().toUpperCase();

const driverDoc =
  driversMap[orig.driverId] ||
  driversByName[driverNameKey];


const driverCompanyName =
  driverDoc?.company || sh.company || "";

const driverShort = shortCompanyName(driverCompanyName);


    const weeks = normalizeWeekKeys(orig);
const total =
  weeks.total ||
  weeks.week1 +
    weeks.week2 +
    weeks.week3 +
    weeks.week4 +
    weeks.week5;

if (!total) return;


    const fromShort = driverShort;
    const toShort = carShort;

    if (!fromShort || !toShort) return;
    if (fromShort === toShort) return;

    const key = `${fromShort}|${toShort}|${platform}`;
    cashSettlementMap[key] = (cashSettlementMap[key] || 0) + total;
  });

});


  // Group BANK settlement by receivable company (toShort)
  const groupedBankSettlementsMap = {};
  Object.entries(bankSettlementMap).forEach(([key, amount]) => {
    const [fromShort, toShort, platform] = key.split("|");
    if (!groupedBankSettlementsMap[toShort]) {
      groupedBankSettlementsMap[toShort] = {
        toShort,
        total: 0,
        rows: [],
      };
    }
    groupedBankSettlementsMap[toShort].rows.push({
      fromShort,
      platform,
      amount,
    });
    groupedBankSettlementsMap[toShort].total += amount;
  });

  const bankSettlementGroups = Object.values(groupedBankSettlementsMap);

  // Group CASH settlement by receivable company (toShort)
  const groupedCashSettlementsMap = {};
  Object.entries(cashSettlementMap).forEach(([key, amount]) => {
    const [fromShort, toShort, platform] = key.split("|");
    if (!groupedCashSettlementsMap[toShort]) {
      groupedCashSettlementsMap[toShort] = {
        toShort,
        total: 0,
        rows: [],
      };
    }
    groupedCashSettlementsMap[toShort].rows.push({
      fromShort,
      platform,
      amount,
    });
    groupedCashSettlementsMap[toShort].total += amount;
  });

  const cashSettlementGroups = Object.values(groupedCashSettlementsMap);
/* ============================================================
   CASH TOTALS + POS ADJUSTMENT
============================================================ */

// ⭐ Total Cash Trips (sum of all cash rows)
const totalCash = cash.reduce((sum, r) => sum + num(r.total), 0);

// ⭐ POS total from bank income section
const posTotal = income.find((i) => i.company === "POS")?.total || 0;

// ⭐ Cash with Driver = Cash Trips – POS received in bank
const balanceCashDriver = totalCash - posTotal;

  /* ============================================================
     5️⃣ RENDER (with fixed column widths)
  ============================================================ */

    return (
    <div style={{ padding: 20 }}>
      {/* HEADER WITH EXPORT BUTTON */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 20 
      }}>
        <h2>📊 Summary Report — {monthLabel}</h2>
        
        <button 
          onClick={handleExportAll} 
          disabled={exporting || sheets.length === 0}
          style={{
            padding: '8px 16px',
            background: exporting || sheets.length === 0 ? '#ccc' : '#0d47a1',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: exporting || sheets.length === 0 ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          {exporting ? 'Exporting...' : '📥 Export All to Excel'}
        </button>
      </div>

      {/* EXPENSES */}
      <h3 style={{ marginTop: 25 }}>Expenses Summary</h3>
      <table style={table}>
        <thead>
          <tr>
            <th style={th}>Expense Head</th>
            <th style={thNum}>Total</th>
          </tr>
        </thead>
        <tbody>
  {expenses.map((e) => (
    <tr key={e.label}>
      <td style={td}>{e.label}</td>
      <td style={tdNum}>{e.total.toFixed(2)}</td>
    </tr>
  ))}

  {/* ⭐ FINAL TOTAL EXPENSES ROW ⭐ */}
  <tr style={{ fontWeight: "bold", background: "#f5f5f5" }}>
    <td style={td}>TOTAL EXPENSES</td>
    <td style={tdNum}>
      {expenses.reduce((sum, r) => sum + Number(r.total || 0), 0).toFixed(2)}
    </td>
  </tr>
</tbody>
      </table>

      {/* BANK INCOME */}
      <h3 style={{ marginTop: 30 }}>Bank Income Summary</h3>
      <table style={table}>
        <thead>
          <tr>
            <th style={th}>Company</th>
            <th style={thNum}>W1</th>
            <th style={thNum}>W2</th>
            <th style={thNum}>W3</th>
            <th style={thNum}>W4</th>
            <th style={thNum}>W5</th>
            <th style={thNum}>Total</th>
          </tr>
        </thead>
        <tbody>
          {income.map((r) => (
            <tr key={r.company}>
              <td style={td}>{r.company}</td>
              <td style={tdNum}>{r.week1.toFixed(2)}</td>
              <td style={tdNum}>{r.week2.toFixed(2)}</td>
              <td style={tdNum}>{r.week3.toFixed(2)}</td>
              <td style={tdNum}>{r.week4.toFixed(2)}</td>
              <td style={tdNum}>{r.week5.toFixed(2)}</td>
              <td style={tdNum}>{r.total.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

              {/* ============================================================
   OVERALL MONTHLY TOTALS — PLACE BEFORE CASH TRIPS
============================================================ */}
{(() => {

  // ⭐ REUSE expenses already calculated above
  const totalExpenses = expenses.reduce((sum, r) => sum + num(r.total), 0);

  // ⭐ Bank Income (summary-level)
  const totalIncomeBank = income.reduce((sum, r) => sum + num(r.total), 0);

  // ⭐ Correct Balance (Income – Expenses)
  const balance = totalIncomeBank - totalExpenses;

  // ⭐ Cash Received/Paid from expenses (summary-level)
  const cashReceivedPaid =
    expenses.find((e) => e.label === "CASH RECEIVED / PAID TO DRIVER")
      ?.total || 0;

  // ⭐ Final Payable/Receivable
  const balancePayable = balance;

  console.log("📊 SUMMARY CALCULATION:");
  console.log("  Total Bank Income:", totalIncomeBank);
  console.log("  Total Expenses:", totalExpenses);
  console.log("  Balance (Income - Expenses):", balance);
  console.log("  Cash Received/Paid:", cashReceivedPaid);
  console.log("  Balance Payable:", balancePayable);

  return (
    <>
      <h3 style={{ marginTop: 30 }}>Income & Balance Summary</h3>

      <table style={table}>
        <tbody>
          <tr>
            <td style={td}>TOTAL INCOME OF THE MONTH</td>
            <td style={tdNum}>{totalIncomeBank.toFixed(2)}</td>
          </tr>

          <tr>
            <td style={td}>BALANCE (Income – Expenses)</td>
            <td style={tdNum}>{balance.toFixed(2)}</td>
          </tr>

          
          <tr>
            <td style={td}>BALANCE PAYABLE / RECEIVABLE</td>
            <td style={tdNum}>{balancePayable.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
    </>
  );
})()}


      {/* CASH TRIPS */}
      <h3 style={{ marginTop: 30 }}>Cash Trips Summary</h3>
      <table style={table}>
        <thead>
          <tr>
            <th style={th}>Company</th>
            <th style={thNum}>W1</th>
            <th style={thNum}>W2</th>
            <th style={thNum}>W3</th>
            <th style={thNum}>W4</th>
            <th style={thNum}>W5</th>
            <th style={thNum}>Total</th>
          </tr>
        </thead>
        <tbody>
  {cash.map((r) => (
    <tr key={r.company}>
      <td style={td}>{r.company}</td>
      <td style={tdNum}>{r.week1.toFixed(2)}</td>
      <td style={tdNum}>{r.week2.toFixed(2)}</td>
      <td style={tdNum}>{r.week3.toFixed(2)}</td>
      <td style={tdNum}>{r.week4.toFixed(2)}</td>
      <td style={tdNum}>{r.week5.toFixed(2)}</td>
      <td style={tdNum}>{r.total.toFixed(2)}</td>
    </tr>
  ))}

  {/* ⭐ TOTAL ROW FOR CASH SUMMARY ⭐ */}
  <tr style={{ fontWeight: "bold", background: "#f5f5f5" }}>
    <td style={td}>TOTAL CASH TRIPS</td>
    <td style={tdNum}>
      {cash.reduce((s, r) => s + r.week1, 0).toFixed(2)}
    </td>
    <td style={tdNum}>
      {cash.reduce((s, r) => s + r.week2, 0).toFixed(2)}
    </td>
    <td style={tdNum}>
      {cash.reduce((s, r) => s + r.week3, 0).toFixed(2)}
    </td>
    <td style={tdNum}>
      {cash.reduce((s, r) => s + r.week4, 0).toFixed(2)}
    </td>
    <td style={tdNum}>
      {cash.reduce((s, r) => s + r.week5, 0).toFixed(2)}
    </td>
    <td style={tdNum}>
      {cash.reduce((s, r) => s + r.total, 0).toFixed(2)}
    </td>
  </tr>
</tbody>

      </table>
{/* ⭐ BALANCE CASH WITH DRIVER ⭐ */}
<h3 style={{ marginTop: 20 }}>Balance Cash With Driver</h3>
<table style={table}>
  <tbody>
    <tr>
      <td style={td}>TOTAL CASH TRIPS</td>
      <td style={tdNum}>{totalCash.toFixed(2)}</td>
    </tr>
    <tr>
      <td style={td}>LESS: POS AMOUNT RECEIVED IN BANK</td>
      <td style={tdNum}>{posTotal.toFixed(2)}</td>
    </tr>
    <tr style={{ fontWeight: "bold", background: "#f5f5f5" }}>
      <td style={td}>BALANCE CASH WITH DRIVER</td>
      <td style={tdNum}>{balanceCashDriver.toFixed(2)}</td>
    </tr>
  </tbody>
</table>

      {/* BANK SETTLEMENT SUMMARY */}
      {bankSettlementGroups.length > 0 && (
        <>
          <h3 style={{ marginTop: 30 }}>Inter-Company Bank Settlement Summary</h3>
          {bankSettlementGroups.map((group) => (
            <div key={group.toShort} style={{ marginTop: 10 }}>
              <div
                style={{
                  fontWeight: "bold",
                  marginBottom: 4,
                  fontSize: 13,
                }}
              >
                {group.toShort} — Receivable from other companies (Total:{" "}
                {group.total.toFixed(2)})
              </div>

              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>From (Bank Holder)</th>
                    <th style={th}>To (Car Company)</th>
                    <th style={th}>Platform / Client</th>
                    <th style={thNum}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row, idx) => (
                    <tr
                      key={`${group.toShort}-bank-${row.platform}-${row.fromShort}-${idx}`}
                    >
                      <td style={td}>{row.fromShort}</td>
                      <td style={td}>{group.toShort}</td>
                      <td style={td}>{row.platform}</td>
                      <td style={tdNum}>{row.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}

      {/* CASH SETTLEMENT SUMMARY */}
      {cashSettlementGroups.length > 0 && (
        <>
          <h3 style={{ marginTop: 30 }}>Inter-Company Cash Settlement Summary</h3>
          {cashSettlementGroups.map((group) => (
            <div key={group.toShort} style={{ marginTop: 10 }}>
              <div
                style={{
                  fontWeight: "bold",
                  marginBottom: 4,
                  fontSize: 13,
                }}
              >
                {group.toShort} — Receivable from other companies (Total:{" "}
                {group.total.toFixed(2)})
              </div>

              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>From (Driver Company)</th>
                    <th style={th}>To (Car Company)</th>
                    <th style={th}>Platform / Client</th>
                    <th style={thNum}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row, idx) => (
                    <tr
                      key={`${group.toShort}-cash-${row.platform}-${row.fromShort}-${idx}`}
                    >
                      <td style={td}>{row.fromShort}</td>
                      <td style={td}>{group.toShort}</td>
                      <td style={td}>{row.platform}</td>
                      <td style={tdNum}>{row.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/* --- styles --- */
const table = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const th = {
  borderBottom: "2px solid #ccc",
  textAlign: "left",
  padding: "6px 4px",
  background: "#f7f7f7",
};

/* NEW — FIXED WIDTH HEADERS FOR NUMERIC COLUMNS */
const thNum = {
  borderBottom: "2px solid #ccc",
  textAlign: "right",
  padding: "6px 4px",
  background: "#f7f7f7",
  width: "80px",
  minWidth: "80px",
  maxWidth: "80px",
};

const td = {
  borderBottom: "1px solid #eee",
  padding: "6px 4px",
};

const tdNum = {
  borderBottom: "1px solid #eee",
  padding: "6px 4px",
  textAlign: "right",
  width: "80px",
  minWidth: "80px",
  maxWidth: "80px",
};